import { logError } from '@shared/log';
import posthog, { type CapturedNetworkRequest } from 'posthog-js';

interface PosthogConfig {
  projectToken: string;
  apiHost: string;
}

const POSTHOG_URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url',
  '$session_referrer',
] as const;

function sanitizeUrl(value: unknown): unknown {
  if (typeof value !== 'string') return value;

  const queryIndex = value.indexOf('?');
  const hashIndex = value.indexOf('#');
  const firstSensitiveIndex = [queryIndex, hashIndex]
    .filter(index => index !== -1)
    .reduce((minimum, index) => Math.min(minimum, index), value.length);
  const baseUrl = value.slice(0, firstSensitiveIndex);

  if (hashIndex === -1) return baseUrl;

  const hash = value.slice(hashIndex);
  if (!hash.startsWith('#/')) return baseUrl;

  const hashQueryIndex = hash.indexOf('?');
  const routeHash = hashQueryIndex === -1 ? hash : hash.slice(0, hashQueryIndex);
  return `${baseUrl}${routeHash}`;
}

function sanitizeUrlProperties(properties: Record<string, unknown>): Record<string, unknown> {
  const sanitizedProperties = { ...properties };

  POSTHOG_URL_PROPERTIES.forEach(property => {
    if (property in sanitizedProperties) {
      sanitizedProperties[property] = sanitizeUrl(sanitizedProperties[property]);
    }
  });

  return sanitizedProperties;
}

function sanitizeEventProperties(properties: Record<string, unknown>): Record<string, unknown> {
  const sanitizedProperties = sanitizeUrlProperties(properties);

  for (const propertyGroup of ['$set', '$set_once'] as const) {
    const propertyValue = sanitizedProperties[propertyGroup];
    if (
      typeof propertyValue === 'object' &&
      propertyValue !== null &&
      !Array.isArray(propertyValue)
    ) {
      sanitizedProperties[propertyGroup] = sanitizeUrlProperties(
        propertyValue as Record<string, unknown>,
      );
    }
  }

  return sanitizedProperties;
}

function sanitizeReplayNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  return {
    ...request,
    name: sanitizeUrl(request.name) as string,
    requestHeaders: undefined,
    requestBody: null,
    responseHeaders: undefined,
    responseBody: null,
  };
}

export function posthogInit(config: PosthogConfig): boolean {
  try {
    // Default config values
    // {
    //   api_host: 'https://app.posthog.com',
    //   api_method: 'POST',
    //   autocapture: true,
    //   rageclick: false,
    //   api_transport: 'XHR',
    //   cookie_expiration: 365,
    //   cross_subdomain_cookie: true,
    //   debug: false,
    //   disable_persistence: false,
    //   ip: true,
    //   opt_out_capturing_by_default: false,
    //   opt_out_persistence_by_default: false,
    //   opt_out_capturing_persistence_type: 'localStorage',
    //   opt_out_capturing_cookie_prefix: null,
    //   persistence: 'cookie',
    //   persistence_name: '',
    //   property_blacklist: [],
    //   secure_cookie: false,
    //   capture_pageview: true,
    //   upgrade: false,
    //   disable_session_recording: false,
    //   xhr_headers: {},
    //   inapp_protocol: '//',
    //   inapp_link_new_window: false,
    //   session_recording: {
    //     blockClass: 'ph-no-capture',
    //     blockSelector: null,
    //     ignoreClass: 'ph-ignore-input',
    //     maskAllInputs: false,
    //     maskInputOptions: {},
    //     maskInputFn: null,
    //     slimDOMOptions: {},
    //     collectFonts: false
    //   },
    //   mask_all_element_attributes: false,
    //   mask_all_text: false
    // }
    posthog.init(config.projectToken, {
      api_host: config.apiHost,
      //Recommended defaults for posthog breaking changes
      //ref: https://posthog.com/docs/libraries/js/config
      defaults: '2026-01-30',
      capture_heatmaps: true,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      secure_cookie: true,
      person_profiles: 'always',
      mask_all_text: true,
      mask_all_element_attributes: true,
      rageclick: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
      },
      capture_performance: {
        network_timing: true,
        web_vitals: true,
      },
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        recordHeaders: false,
        recordBody: false,
        captureCanvas: { recordCanvas: false },
        recordCrossOriginIframes: false,
        maskCapturedNetworkRequestFn: sanitizeReplayNetworkRequest,
      },
      before_send: event => {
        if (!event) return null;
        return {
          ...event,
          properties: sanitizeEventProperties(event.properties),
          $set: event.$set ? sanitizeUrlProperties(event.$set) : event.$set,
          $set_once: event.$set_once ? sanitizeUrlProperties(event.$set_once) : event.$set_once,
        };
      },
    });
    return true;
  } catch (error) {
    logError(posthogInit, 'Error initializing PostHog:', { error });
    return false;
  }
}

export function posthogIdentifyUser(userId: string, properties: Record<string, any>) {
  try {
    posthog.identify(userId, properties);
  } catch (error) {
    logError(posthogIdentifyUser, 'Error identifying user in PostHog:', { error });
  }
}

export function posthogReset() {
  try {
    posthog.reset();
  } catch (error) {
    logError(posthogReset, 'Error resetting PostHog:', { error });
  }
}
